import { CalendarCheck, CheckCircle2, FileText, GraduationCap, Mail, PlayCircle, XCircle } from 'lucide-react';
import { createElement } from 'react';
import type { LifecycleEvent } from '../../../../components/LifecycleTimeline';
import { addressPathLabel } from '../../../../lib/addressApi';
import { formatDateOnly } from '../../../../lib/formatDate';
import { TRAINING_DECISION, TRAINING_RESULT, type MySurveyTrainingRecommendation } from '../mySurveyTypes';

/** 16px, which is what `<LifecycleTimeline>`'s rail is sized for. */
const glyph = (icon: typeof FileText) => createElement(icon, { className: 'h-4 w-4' });

/**
 * One training recommendation's life cycle, as the employee it was sent to lived it (product call,
 * 13 Oct 2026): sent → accepted or declined → enrolled → training started → training completed or cancelled →
 * report submitted. The steps are the STATUS's own words.
 *
 * DERIVED from instants the track already carries — nothing is stored for the timeline, so there
 * is no second history to fall out of step. A step that has NOT happened has no instant and is not
 * drawn; the banner says what is next. No actor: every step after "sent" is the reader's own.
 * `!= null` throughout: every field came off the wire, where a null is omitted.
 */
export function myTrainingRecommendationLifecycle(recommendation: MySurveyTrainingRecommendation): LifecycleEvent[] {
  const events: LifecycleEvent[] = [
    // It is listed at all only because it was sent, so this step is always there.
    { key: 'sent', label: 'Sent', at: recommendation.sentAt, icon: glyph(Mail), tone: 'blue' },
  ];

  if (recommendation.decidedAt != null && recommendation.decision === TRAINING_DECISION.Accepted) {
    events.push({ key: 'accepted', label: 'Accepted', at: recommendation.decidedAt, icon: glyph(CheckCircle2), tone: 'emerald' });
  }

  if (recommendation.decidedAt != null && recommendation.decision === TRAINING_DECISION.Declined) {
    events.push({
      key: 'declined',
      label: 'Declined',
      at: recommendation.decidedAt,
      icon: glyph(XCircle),
      tone: 'red',
      detail: recommendation.declineReason ?? undefined,
    });
  }

  const enrollment = recommendation.enrollment;

  // A training started before the Enroll step existed has a school and no instant — nothing to place.
  if (enrollment?.enrolledAt != null) {
    const location = addressPathLabel(enrollment);

    events.push({
      key: 'enrolled',
      label: 'Enrolled',
      at: enrollment.enrolledAt,
      icon: glyph(GraduationCap),
      tone: 'blue',
      detail: location ? `${enrollment.school} — ${location}` : enrollment.school,
    });
  }

  const training = recommendation.training;

  if (training != null) {
    events.push({
      key: 'started',
      label: 'Training started',
      at: training.recordedAt,
      icon: glyph(PlayCircle),
      tone: 'amber',
      // Calendar dates — `formatDateOnly`, never the instant path.
      detail: `${formatDateOnly(training.startDate)} – ${formatDateOnly(training.endDate)}`,
    });

    if (training.completedAt != null) {
      const cancelled = training.result === TRAINING_RESULT.Cancelled;

      events.push({
        key: 'ended',
        label: cancelled ? 'Training cancelled' : 'Training completed',
        at: training.completedAt,
        icon: glyph(cancelled ? XCircle : CalendarCheck),
        tone: cancelled ? 'red' : 'emerald',
        detail: training.completionRemarks ?? undefined,
      });
    }

    if (training.reportAt != null) {
      events.push({ key: 'report', label: 'Report submitted', at: training.reportAt, icon: glyph(FileText), tone: 'emerald' });
    }
  }

  return events;
}
